'use client';

import React, { useState, useCallback, useEffect } from 'react';
import type { ChatMessage } from '@chewy/chat-library-common';
import { HeartBurstAnimation, HEART_COLORS } from '@chewy/chat-library-components';
import styles from './HeartBurstMessage.module.scss';

export interface HeartBurstMessagePayload {
  autoPlay?: boolean;
  mainHeartSize?: number;
  mainHeartColor?: string;
}

export function HeartBurstMessage({
  message
}: {
  message?: ChatMessage<HeartBurstMessagePayload>
}) {
  const { autoPlay = true, mainHeartSize = 60, mainHeartColor = HEART_COLORS.FILL_1 } = message?.payload ?? {};
  const [trigger, setTrigger] = useState(false);

  const handleTrigger = useCallback(() => {
    setTrigger(true);
  }, []);

  const handleComplete = useCallback(() => {
    setTrigger(false);
  }, []);

  useEffect(() => {
    if (autoPlay) {
      const timer = setTimeout(() => {
        setTrigger(true);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [autoPlay]);

  return (
    <div
      className={styles.container}
      onClick={handleTrigger}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && handleTrigger()}
      aria-label="Play heart animation"
    >
      <HeartBurstAnimation
        trigger={trigger}
        onComplete={handleComplete}
        mainHeartSize={mainHeartSize}
        mainHeartColor={mainHeartColor}
      />
      {!trigger && (
        <div className={styles.staticHeart}>
          <svg width={mainHeartSize} height={mainHeartSize} viewBox="0 0 24 24" fill={mainHeartColor}>
            <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
          </svg>
        </div>
      )}
    </div>
  );
}

export default HeartBurstMessage;
